import React, { useState, useRef, useEffect } from 'react';
import { toast } from 'react-toastify';
import RichTextEditor from '../TextEditor/components/RichTextEditor';
import { serialize } from '../TextEditor/utils/htmlSerializer';
import { newsService } from '../../services/newsService';
import { format, parseISO } from 'date-fns';
import { PageHeader, Modal, EmptyState, PageSpinner, ConfirmDialog, StatCardRow, Toolbar, ToolbarField } from '../admin';

const TAGS_OPTIONS = [
  'MAFA',
  'CAFA',
  'Division 1',
  'Division 2',
  'Division 3',
  'Division 4',
  'National League',
  'Zambia Super League'
];

const DEFAULT_EDITOR_CONTENT = [{
  type: 'paragraph',
  children: [{ text: '' }],
}];

const htmlToSlate = (html) => {
  if (!html) return DEFAULT_EDITOR_CONTENT;
  
  const tempDiv = document.createElement('div');
  tempDiv.innerHTML = html;
  
  const nodes = [];
  
  Array.from(tempDiv.childNodes).forEach(node => {
    if (node.nodeType === Node.TEXT_NODE) {
      if (node.textContent.trim()) {
        nodes.push({
          type: 'paragraph',
          children: [{ text: node.textContent }]
        });
      }
    } else if (node.nodeType === Node.ELEMENT_NODE) {
      if (node.tagName === 'P') {
        nodes.push({
          type: 'paragraph',
          children: [{ text: node.textContent }]
        });
      } else if (node.tagName === 'IMG') {
        nodes.push({
          type: 'image',
          url: node.src,
          alt: node.alt,
          children: [{ text: '' }]
        });
      } else if (node.tagName === 'VIDEO') {
        const source = node.querySelector('source');
        if (source) {
          nodes.push({
            type: 'video',
            url: source.src,
            children: [{ text: '' }]
          });
        }
      }
    }
  });
  
  return nodes.length > 0 ? nodes : DEFAULT_EDITOR_CONTENT;
};

// Ensure we always have valid editor content
const getValidEditorContent = (content) => {
  if (!content || !Array.isArray(content) || content.length === 0) {
    return DEFAULT_EDITOR_CONTENT;
  }
  return content;
};

const News = () => {
  const [activeTab, setActiveTab] = useState('create'); // 'create' or 'manage'
  const [articleSearch, setArticleSearch] = useState('');
  const [deleting, setDeleting] = useState(null);
  const [headline, setHeadline] = useState('');
  const [tags, setTags] = useState([]);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [showPreview, setShowPreview] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(null);
  const [editorContent, setEditorContent] = useState(() => getValidEditorContent(DEFAULT_EDITOR_CONTENT));
  const [newsArticles, setNewsArticles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editingArticle, setEditingArticle] = useState(null);
  const [showMediaControls, setShowMediaControls] = useState(false);
  const [selectedMedia, setSelectedMedia] = useState(null);
  const MAX_HEADLINE_LENGTH = 150;

  // Fetch news articles when switching to manage tab
  useEffect(() => {
    if (activeTab === 'manage') {
      fetchNewsArticles();
    }
  }, [activeTab]);

  const fetchNewsArticles = async () => {
    setLoading(true);
    try {
      const data = await newsService.getAllNews();
      setNewsArticles(data);
    } catch (error) {
      setError('Failed to fetch news articles');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    try {
      const date = parseISO(dateString);
      if (isNaN(date.getTime())) {
        return 'Date not available';
      }
      return format(date, 'MMMM dd, yyyy');
    } catch (error) {
      console.error('Error formatting date:', error);
      return 'Date not available';
    }
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const toggleTag = (tag) => {
    setTags(prevTags => 
      prevTags.includes(tag)
        ? prevTags.filter(t => t !== tag)
        : [...prevTags, tag]
    );
  };

  const removeTag = (tagToRemove) => {
    setTags(prevTags => prevTags.filter(tag => tag !== tagToRemove));
  };

  const validateForm = () => {
    if (!headline.trim()) {
      setError('Headline is required');
      return false;
    }
    if (headline.length > MAX_HEADLINE_LENGTH) {
      setError(`Headline must be less than ${MAX_HEADLINE_LENGTH} characters`);
      return false;
    }
    if (editorContent.length === 1 && editorContent[0].children[0].text === '') {
      setError('Content is required');
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!validateForm()) return;

    setIsSubmitting(true);
    try {
      const url = editingArticle 
        ? `${process.env.REACT_APP_API_URL}/news/${editingArticle.id}`
        : `${process.env.REACT_APP_API_URL}/news`;
      
      const method = editingArticle ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          headline: headline.trim(),
          content: serialize(editorContent),
          tags: tags,
          published_at: new Date().toISOString()
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to save news article');
      }

      setHeadline('');
      setEditorContent([{ type: 'paragraph', children: [{ text: '' }] }]);
      setTags([]);
      setShowPreview(false);
      setEditingArticle(null);
      toast.success(`News article ${editingArticle ? 'updated' : 'published'} successfully!`);
      
      if (activeTab === 'manage') {
        fetchNewsArticles();
      }
    } catch (error) {
      console.error('Error saving news article:', error);
      setError(`Failed to ${editingArticle ? 'update' : 'create'} news article. Please try again.`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEdit = (article) => {
    setEditingArticle(article);
    setHeadline(article.headline);
    setTags(article.tags || []);
    setEditorContent(htmlToSlate(article.content));
    setActiveTab('create');
  };

  const handleDelete = async () => {
    if (!deleting) return;
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/news/${deleting.id}`, {
        method: 'DELETE',
      });

      if (!response.ok) {
        throw new Error('Failed to delete news article');
      }

      setNewsArticles(prevArticles => prevArticles.filter(article => article.id !== deleting.id));
      setDeleting(null);
    } catch (error) {
      console.error('Error deleting news article:', error);
      setError('Failed to delete news article. Please try again.');
    }
  };

  const handleCancel = () => {
    setEditingArticle(null);
    setHeadline('');
    setTags([]);
    setEditorContent([{ type: 'paragraph', children: [{ text: '' }] }]);
    setShowPreview(false);
  };

  // Add function to handle media removal
  const handleRemoveMedia = () => {
    if (!selectedMedia) return;

    const newContent = editorContent.filter(node => {
      if (node.type === 'image' || node.type === 'video') {
        return node.url !== selectedMedia.url;
      }
      return true;
    });

    setEditorContent(newContent);
    setShowMediaControls(false);
    setSelectedMedia(null);
  };

  // Add function to handle media selection
  const handleMediaClick = (media) => {
    setSelectedMedia(media);
    setShowMediaControls(true);
  };

  /* Derived: search-filtered article list for the manage tab */
  const filteredArticles = newsArticles.filter(
    (a) => !articleSearch || a.headline?.toLowerCase().includes(articleSearch.toLowerCase())
  );

  return (
    <div className="space-y-4">
      <PageHeader
        title="Sports News"
        description="Write, publish and manage news articles"
      />

      {/* Tab strip with counts */}
      <div className="flex items-center justify-between border-b border-line">
        <div className="flex gap-1">
          {[
            { id: 'create', label: 'Compose' },
            { id: 'manage', label: 'Manage' },
          ].map((tab) => (
            <button
              key={tab.id}
              type="button"
              onClick={() => setActiveTab(tab.id)}
              className={`px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors ${
                activeTab === tab.id
                  ? 'border-accent text-white'
                  : 'border-transparent text-gray-400 hover:text-white'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
        {activeTab === 'create' && (
          <button
            type="button"
            onClick={() => setShowPreview(!showPreview)}
            className="btn-secondary !py-1.5 !px-3 mb-1.5"
          >
            {showPreview ? 'Edit' : 'Preview'}
          </button>
        )}
      </div>

      {activeTab === 'manage' && (
        <StatCardRow
          stats={[
            { label: 'Total articles', value: newsArticles.length },
            {
              label: 'Published (7 days)',
              value: newsArticles.filter(
                (a) => a.published_at && Date.now() - new Date(a.published_at) < 7 * 864e5
              ).length,
            },
            {
              label: 'Tagged',
              value: newsArticles.filter((a) => a.tags?.length > 0).length,
            },
          ]}
        />
      )}

      {error && (
        <div className="panel px-4 py-3 text-sm text-accent-soft border-accent-dim">
          {error}
        </div>
      )}

      {activeTab === 'create' ? (
        <>
          {editingArticle && (
            <div className="panel px-4 py-3 text-sm text-accent-soft border-accent-dim">
              Editing article: {editingArticle.headline}
            </div>
          )}

          {showPreview ? (
            <div className="card p-6 space-y-6">
              <h1 className="text-2xl font-bold text-white">{headline}</h1>
              <div 
                className="prose prose-invert max-w-none"
                dangerouslySetInnerHTML={{ __html: serialize(editorContent) }}
              />
              {tags.length > 0 && (
                <div className="flex gap-2 flex-wrap">
                  {tags.map((tag, index) => (
                    <span key={index} className="badge-accent">{tag}</span>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="card p-6 space-y-5">
              <div>
                <div className="flex justify-between items-center mb-2">
                  <label htmlFor="headline" className="field-label">
                    Headline
                  </label>
                  <span className={`text-sm ${headline.length > MAX_HEADLINE_LENGTH ? 'text-accent-soft' : 'text-gray-500'}`}>
                    {headline.length}/{MAX_HEADLINE_LENGTH}
                  </span>
                </div>
                <input
                  type="text"
                  id="headline"
                  value={headline}
                  onChange={(e) => setHeadline(e.target.value)}
                  className="field"
                  placeholder="Enter compelling news headline"
                  required
                />
              </div>

              <div>
                <label className="field-label mb-2">
                  Content
                </label>
                <RichTextEditor
                  onChange={setEditorContent}
                  initialContent={getValidEditorContent(editorContent)}
                  uploadProgress={uploadProgress}
                  onMediaClick={(media) => {
                    setSelectedMedia(media);
                    setShowMediaControls(true);
                  }}
                />
              </div>

              <div className="relative" ref={dropdownRef}>
                <label className="field-label mb-2">
                  Tags
                </label>
                <div className="relative">
                  <button
                    type="button"
                    onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                    className="field text-left flex items-center justify-between"
                  >
                    <span className="truncate">
                      {tags.length > 0 ? `${tags.length} tags selected` : 'Select tags'}
                    </span>
                    <svg
                      className={`w-5 h-5 transition-transform ${isDropdownOpen ? 'rotate-180' : ''}`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                  
                  {isDropdownOpen && (
                    <div className="absolute z-10 w-full mt-1 bg-surface-800 border border-line-strong rounded-lg shadow-2xl max-h-60 overflow-y-auto">
                      {TAGS_OPTIONS.map((tag) => (
                        <div
                          key={tag}
                          className="px-4 py-2 hover:bg-surface-700 cursor-pointer flex items-center"
                          onClick={() => toggleTag(tag)}
                        >
                          <input
                            type="checkbox"
                            checked={tags.includes(tag)}
                            onChange={() => {}}
                            className="mr-2 h-4 w-4 text-accent border-line-strong rounded focus:ring-accent"
                          />
                          <span className="text-white">{tag}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                
                {tags.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-2">
                    {tags.map((tag) => (
                      <span key={tag} className="badge-accent">
                        {tag}
                        <button
                          type="button"
                          onClick={() => removeTag(tag)}
                          className="ml-2 hover:text-white"
                        >
                          ×
                        </button>
                      </span>
                    ))}
                  </div>
                )}
              </div>

              <div className="flex gap-3">
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className={`btn-primary flex-1 !py-3 ${isSubmitting ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  {isSubmitting ? (
                    <span className="flex items-center justify-center">
                      <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      {editingArticle ? 'Updating…' : 'Publishing…'}
                    </span>
                  ) : (
                    editingArticle ? 'Update News' : 'Publish News'
                  )}
                </button>
                {editingArticle && (
                  <button
                    type="button"
                    onClick={handleCancel}
                    className="btn-secondary !py-3"
                  >
                    Cancel
                  </button>
                )}
              </div>
            </form>
          )}
        </>
      ) : (
        <div className="space-y-4">
          <Toolbar>
            <ToolbarField label="Search articles">
              <input
                type="text"
                value={articleSearch}
                onChange={(e) => setArticleSearch(e.target.value)}
                placeholder="Search by headline…"
                className="field"
              />
            </ToolbarField>
            {articleSearch && (
              <button type="button" className="btn-ghost" onClick={() => setArticleSearch('')}>
                Clear
              </button>
            )}
          </Toolbar>

          {loading ? (
            <div className="card">
              <PageSpinner label="Loading articles…" />
            </div>
          ) : filteredArticles.length === 0 ? (
            <div className="card">
              <EmptyState
                title={articleSearch ? 'No articles match your search' : 'No news articles found'}
                message={articleSearch ? 'Try a different headline.' : 'Published articles will appear here.'}
              />
            </div>
          ) : (
            filteredArticles.map((article) => (
              <div
                key={article.id}
                className="card p-4"
              >
                <div className="flex justify-between items-start gap-4">
                  <div className="min-w-0">
                    <h3 className="text-base font-semibold text-white">{article.headline}</h3>
                    <div className="flex items-center gap-2 mt-1">
                      <p className="text-gray-500 text-sm">{formatDate(article.published_at)}</p>
                      {article.tags?.slice(0, 3).map((tag, index) => (
                        <span key={index} className="badge-neutral">{tag}</span>
                      ))}
                      {article.tags?.length > 3 && (
                        <span className="text-xs text-gray-600">+{article.tags.length - 3} more</span>
                      )}
                    </div>
                  </div>
                  <div className="flex gap-1 shrink-0">
                    <button
                      type="button"
                      onClick={() => handleEdit(article)}
                      className="btn-ghost !py-1.5 !px-3"
                    >
                      Edit
                    </button>
                    <button
                      type="button"
                      onClick={() => setDeleting(article)}
                      className="btn-danger !py-1.5 !px-3"
                    >
                      Delete
                    </button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {deleting && (
        <ConfirmDialog
          title="Delete article"
          message={`Delete "${deleting.headline}"? This cannot be undone.`}
          onConfirm={handleDelete}
          onCancel={() => setDeleting(null)}
        />
      )}

      {showMediaControls && selectedMedia && (
        <Modal title="Media controls" onClose={() => setShowMediaControls(false)}>
          <div className="space-y-4">
            <div className="panel p-4 flex items-center justify-center">
              {selectedMedia.type === 'image' ? (
                <img 
                  src={selectedMedia.url} 
                  alt={selectedMedia.alt || 'Selected image'} 
                  className="max-w-full h-auto rounded-lg"
                />
              ) : (
                <video 
                  src={selectedMedia.url} 
                  controls 
                  className="max-w-full rounded-lg"
                />
              )}
            </div>
            <div className="flex gap-3">
              <button
                type="button"
                onClick={handleRemoveMedia}
                className="btn-primary flex-1"
              >
                Remove Media
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowMediaControls(false);
                  setSelectedMedia(null);
                }}
                className="btn-secondary flex-1"
              >
                Cancel
              </button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
};

export default News; 